import React, { useCallback, useMemo } from 'react'
import InvalidFeedback from '../../../../../inplant-components-fe/ui/components/Input/InvalidFeedback'
import {
  useDispatchEditChecklist,
  useEditChecklistComponentMetadata,
} from '../../../../hook/useEditChecklist'
import { useTranslation } from 'react-i18next'
import { EditComponentMetadataError } from '../../../../redux/reducers/checklistEdit'
import { useDidUpdate } from '../../../../../inplant-components-fe/mvfunctions/hooks'

/**
 * Identity function.
 */
function _id<T>(value: unknown): T {
  return value as T
}

interface FormComponentBuilderChildrenProps<T> {
  className: string
  onBlur: () => void
  updating: boolean
  value: T
  disabled: boolean
}

interface SimpleComponent {
  id: string
}

interface Props<T> {
  /**
   * custom class name to add to the form group.
   */
  className?: string
  /**
   * The component reference.
   */
  component: SimpleComponent

  children: (props: FormComponentBuilderChildrenProps<T>) => JSX.Element
  /**
   * {@link SimpleComponent} value can be different from expected type.
   * This function is used to parse the value to the expected type.
   */
  valueParser?: (value: unknown) => T

  /**
   * Fired when an error is received.
   */
  onErrorReceived?: (error: EditComponentMetadataError) => void
}
/**
 * Build a `form-group` component for checklist component {@link Props.component.id}
 *
 * Using {@link useEditChecklistComponentMetadata} internally handle invalid feedback
 */
function FormComponentBuilder<T>(props: Props<T>): JSX.Element {
  const metadata = useEditChecklistComponentMetadata(props.component.id)

  const { setComponentBlurred } = useDispatchEditChecklist()
  const { t } = useTranslation()

  const uiError = useMemo(() => {
    if (!metadata.showError || !metadata.error) {
      return null
    }

    if (metadata.error?.type === 'server') {
      // currently BE return un-localized error messages so a default translation is used
      return t('checklist.components.invalid')
    }

    if (metadata.error.slug === 'required') {
      return t('checklist.components.required')
    }

    return t('checklist.components.required') // fallback
  }, [metadata.showError, metadata.error, t])

  const handleBlur = useCallback(() => {
    setComponentBlurred(props.component.id)
  }, [props.component.id, setComponentBlurred])

  const value = useMemo(() => {
    if (props.valueParser) {
      return props.valueParser(metadata.value)
    }

    return _id<T>(metadata.value)
  }, [metadata.value, props.valueParser])

  useDidUpdate(() => {
    if (metadata.error && props.onErrorReceived) {
      props.onErrorReceived(metadata.error)
    }
  }, [metadata.error])

  

  return (
    <div className={`form-group ${props.className ?? ''}`}>
      {props.children({
        className: `${uiError ? 'is-invalid' : ''}`,
        onBlur: handleBlur,
        updating: metadata.updating,
        value,
        disabled: metadata.updating || metadata.disabled,
      })}
      {uiError ? <InvalidFeedback error={uiError} /> : null}
    </div>
  )
}

export type {FormComponentBuilderChildrenProps}
export default FormComponentBuilder
